import type { Degree, Distance, Point } from "@pdfking/math"
import { Max, Min, Scale, Subtract, Total } from "@pdfking/math"
import { QUARTER_TURN, ZERO_DEGREE, ZERO_DISTANCE } from "@pdfking/math/constants"
import { MM } from "@pdfking/quantity"
import { ArcPoints } from "@pdfking/renderer-api"
import { Constant } from "@pdfking/tfunc"

import type { FolderSide } from "#internal"
import {
  FolderAt as corner,
  FolderCardSlits,
  FolderCentered,
  FolderChain,
  FolderCuts,
  FolderDieOf,
  FolderFlap,
  FolderFlapOff,
  FolderInside,
  FolderLine,
  FolderLines,
  FolderMirror,
  FolderOutline,
  FolderSlot,
  FolderSpine,
  FolderSpecList as S,
} from "#internal"
import type { FolderDie, FolderPath, FolderTemplate, PrimaryKey } from "#public"
import { RecordByPrimaryKey } from "#public"

type Draw = FolderTemplate["draw"]

const folder = <I extends string>(
  id: I,
  size: [Distance, Distance],
  draw: Draw
): FolderTemplate & PrimaryKey<I> => ({ id, size, draw })

const at = (x: Distance, y: Distance): Point<Distance> => ({ x, y })

const UP = Scale(QUARTER_TURN, -1)
const ZERO = ZERO_DISTANCE

// A glue tab `depth` deep, its ends cut in `bevel`, past a gusset.
const glue = (depth: Distance, gusset: Distance, bevel: [Distance, Distance] = [MM(5), MM(5)]) =>
  FolderFlap(depth, { gusset, taper: bevel })

// The two covers round the spine, `x` from the die's left, `top` from its
// top, flaps on the back's edges: the outline from the back's top left
// corner clockwise, and the spine's folds.
type BackFlaps = { top: FolderSide; bottom: FolderSide; left: FolderSide }
const covers = (
  x: Distance,
  top: Distance,
  width: Distance,
  height: Distance,
  spine: Distance,
  { top: up, bottom, left }: Partial<BackFlaps> = {},
  middle = false
) => {
  const [back, front, base] = [Total(x, width), Total(x, width, width, spine), Total(top, height)]
  const edge = (side: FolderSide | undefined, a: Point<Distance>, b: Point<Distance>) =>
    side ? side(a, b) : FolderChain(corner(a.x, a.y), corner(b.x, b.y))
  return FolderChain(
    edge(up, at(x, top), at(back, top)),
    corner(front, top),
    corner(front, base),
    edge(bottom, at(back, base), at(x, base)),
    edge(left, at(x, base), at(x, top)),
    FolderSpine(back, top, base, spine, middle)
  )
}

// A plain folder, `extra` cut into it.
const plain = (
  width: Distance,
  height: Distance,
  spine: Distance,
  ...extra: FolderPath[]
): FolderDie =>
  FolderDieOf(
    Total(width, width, spine),
    height,
    covers(ZERO, ZERO, width, height, spine),
    FolderCuts(...extra)
  )

// The archive folder's thumb notches, cut into the front by the spine at `x`,
// as far down the cover as the template's; none where they would meet.
const NOTCH = MM(15.25)
const notches = (x: Distance, height: Distance) => {
  const [a, b] = [Scale(height, 69 / 310), Scale(height, 0.81)]
  return a < Total(NOTCH, MM(5)) || Subtract(b, a) < Total(NOTCH, NOTCH, MM(10))
    ? []
    : [FolderSlot(at(x, a), ZERO_DEGREE, NOTCH), FolderSlot(at(x, b), ZERO_DEGREE, NOTCH)]
}

// The archive folder's label window, 91 x 42 mm, in the front's top right
// corner ending at `right`; none on a front too small for it by the notches.
const window = (right: Distance, front: Distance, height: Distance) => {
  const [x, y, r] = [Subtract(right, MM(121.3)), MM(24.8), MM(5)]
  const [x2, y2] = [Total(x, MM(91)), Total(y, MM(42))]
  return x < Total(front, NOTCH, MM(10)) || y2 > Scale(height, 0.5)
    ? []
    : [FolderOutline([corner(x, y, r), corner(x2, y, r), corner(x2, y2, r), corner(x, y2, r)])]
}

// A pocket's rounded open corner, a semicircle's: as round as it fits.
const ROUND = MM(1e4)

// The quarter round pocket folded over the back locked by a tab under it:
// Frutiger, in each size its template has its own lengths for.
type Lock = {
  // The pocket, and its open corner's radius.
  pocket: [width: Distance, height: Distance, radius: Distance]
  // The flap under the back: how short of the spine, how deep, how round.
  flap: [short: Distance, depth: Distance, radius: Distance]
  // The tab at the flap's middle, and the slit in the pocket it locks in,
  // that far from the pocket's fold.
  tab: [width: Distance, depth: Distance, radius: Distance]
  slit: [length: Distance, from_fold: Distance]
}
const frutiger =
  ([cover_width, cover_height]: [Distance, Distance], lock: Lock): Draw =>
  (width, height, spine) => {
    const [pocket_width, pocket_height] = [
      Scale(width, lock.pocket[0] / cover_width),
      Scale(height, lock.pocket[1] / cover_height),
    ]
    const x = Total(pocket_width, spine)
    const flap_width = Subtract(width, lock.flap[0])
    const [, flap_depth, flap_round] = lock.flap
    const [, tab_depth, tab_round] = lock.tab
    const pocket = FolderFlapOff("left", pocket_width, {
      gusset: spine,
      round: [lock.pocket[2], ZERO],
    })(at(x, height), at(x, Subtract(height, pocket_height)))
    // A flap too short for the tab takes a narrower one, and its slit.
    const tab = Min(lock.tab[0], Subtract(flap_width, Total(flap_round, flap_round, MM(10))))
    const slit_length = Total(Subtract(lock.slit[0], lock.tab[0]), tab)
    const flap = FolderFlapOff("bottom", flap_depth, {
      gusset: spine,
      round: [flap_round, flap_round],
      tip: FolderCentered(
        tab,
        FolderFlap(tab_depth, { folded: false, round: [tab_round, tab_round] })
      ),
    })
    // Folded over the back, the slit lands on the tab.
    const slit = Subtract(pocket_width, Scale(flap_width, 0.5))
    const slit_y = Subtract(height, lock.slit[1])
    return FolderDieOf(
      Total(x, width, width, spine),
      Total(height, spine, flap_depth, tab_depth),
      covers(x, ZERO, width, height, spine, {
        bottom: () => flap(at(Total(x, flap_width), height), at(x, height)),
        left: Constant(pocket),
      }),
      FolderCuts(
        ...FolderLines(pocket.corners, [
          at(Subtract(slit, Scale(slit_length, 0.5)), slit_y),
          at(Total(slit, Scale(slit_length, 0.5)), slit_y),
        ])
      )
    )
  }

// Three flaps round the back, on gussets: the side one across, the top and
// bottom ones alike, each `depth` the share of the cover it is deep, then
// how its sides draw in (the share of its depth), how round its corners and
// how far its base is in from the back's sides, each pair as the grow walks
// the edge.
type Flap = {
  depth: number
  taper: readonly [number, number]
  round: [Distance, Distance]
  inset: [Distance, Distance]
  bevel: [boolean, boolean]
}
const three_flaps =
  (side: Flap, ends: Flap, middle = false, back_extra: Distance = ZERO_DISTANCE): Draw =>
  (width, height, spine) => {
    const [side_depth, end_depth] = [Scale(width, side.depth), Scale(height, ends.depth)]
    const x = Total(side_depth, spine)
    const top = Total(end_depth, spine)
    const back = Total(width, back_extra)
    const right = Total(x, back, spine, width)
    const bottom = Total(top, height)
    // A bevelled side runs across the gusset too.
    const flap = (
      edge: "top" | "bottom" | "left",
      { taper, round, bevel }: Flap,
      depth: Distance
    ) =>
      FolderFlapOff(edge, depth, {
        gusset: spine,
        middle,
        taper: [
          Scale(bevel[0] ? Total(depth, spine) : depth, taper[0]),
          Scale(bevel[1] ? Total(depth, spine) : depth, taper[1]),
        ],
        round,
        bevel,
      })
    const left_end = Total(x, ends.inset[0])
    const right_end = Subtract(Total(x, back), ends.inset[1])
    return FolderDieOf(
      right,
      Total(top, top, height),
      FolderChain(
        corner(x, top),
        flap("top", ends, end_depth)(at(left_end, top), at(right_end, top)),
        corner(right, top),
        corner(right, bottom),
        flap("bottom", ends, end_depth)(at(right_end, bottom), at(left_end, bottom)),
        flap("left", side, side_depth)(at(x, bottom), at(x, top)),
        FolderSpine(Total(x, back), top, bottom, spine, middle)
      )
    )
  }

// Rounded pockets glued under the back, the front or both, by the inner
// panel folded over the back when there is one (`inner`, its share of the
// cover): each pocket's share of the cover's width and its glue tab's depth,
// the tab under the inner panel or out of the die's side. Under both covers
// each cover's stack has half the spine; the left pocket, under the panel,
// stands on a gusset short of the panel's (FolderSpecList).
type Pocket = { width: number; glue: Distance }
const pockets =
  (inner: number, left: Pocket | undefined, right: Pocket | undefined): Draw =>
  (width, height, spine, sheet) => {
    const short = (gusset: Distance, sheets: number) =>
      Max(ZERO, Subtract(gusset, Scale(sheet, sheets)))
    const top = left && right ? short(Scale(spine, 0.5), S.shared_spine) : spine
    const under = inner ? short(top, S.under_panel) : top
    const depth = Scale(height, S.glued_pocket.depth)
    const panel = Scale(width, inner)
    const x = panel ? Total(panel, top) : Total(under, left?.glue ?? ZERO)
    const edge = Total(x, width, width, spine)
    const round = MM(65)
    return FolderDieOf(
      right ? Total(edge, top, right.glue) : edge,
      Total(height, right ? top : under, depth),
      corner(x, ZERO),
      corner(edge, ZERO),
      right
        ? FolderFlapOff("bottom", depth, {
            gusset: top,
            round: [round, ZERO],
            end: glue(right.glue, top),
          })(at(edge, height), at(Subtract(edge, Scale(width, right.width)), height))
        : corner(edge, height),
      left
        ? FolderFlapOff("bottom", depth, {
            gusset: under,
            round: [ZERO, round],
            start: glue(left.glue, under),
          })(at(Total(x, Scale(width, left.width)), height), at(x, height))
        : corner(x, height),
      ...(panel ? [FolderFlapOff("left", panel, { gusset: top })(at(x, height), at(x, ZERO))] : []),
      FolderSpine(Total(x, width), ZERO, height, spine)
    )
  }

/** The static folder templates of the shop (`prints.SheetSize`, the `.eps`
 * attachments of the folder products), each drawn at any cover round any
 * spine as its template is at its own: the outside printed side. */
export const FolderTemplateList = RecordByPrimaryKey([
  // Archive folders: two thumb notches by the spine, a window on the front.
  folder("arial_narrow", [MM(220), MM(310)], (width, height, spine) =>
    plain(
      width,
      height,
      spine,
      ...notches(Total(width, spine), height),
      ...window(Total(width, width, spine), Total(width, spine), height)
    )
  ),

  folder("arial_rounded", [MM(220), MM(310)], (width, height, spine) =>
    plain(width, height, spine, ...notches(Total(width, spine), height))
  ),

  // A half round thumb cut by the back's outer edge, one by its bottom.
  folder("bauhaus", [MM(222), MM(311.8)], (width, height, spine) =>
    plain(
      width,
      height,
      spine,
      FolderSlot(at(MM(6.3), Scale(height, 0.5)), ZERO_DEGREE, MM(9.5)),
      FolderSlot(at(Scale(width, 112.85 / 222), Subtract(height, MM(5.9))), UP, MM(9.5))
    )
  ),

  folder("verlag", [MM(227), MM(315)], (width, height, spine) =>
    plain(
      width,
      height,
      spine,
      FolderSlot(at(MM(9.8), Scale(height, 0.5)), ZERO_DEGREE, MM(9.75), MM(7.4)),
      FolderSlot(at(Scale(width, 116.35 / 227), Subtract(height, MM(9.1))), UP, MM(9.75), MM(7.4))
    )
  ),

  // An index tab out of the back, the front's corner cut away round it, at
  // 45°.
  folder("didot", [MM(220), MM(310)], (width, height, spine) => {
    const [tab, cut] = [MM(S.didot.tab), MM(25.6)]
    const right = Total(tab, width, width, spine)
    const [left_y, right_y] = [Scale(height, S.didot.tab_at), Scale(height, 88.3 / 310)]
    return FolderDieOf(
      right,
      height,
      corner(ZERO, ZERO, MM(10)),
      corner(Subtract(right, cut), ZERO, MM(7.8)),
      corner(Subtract(right, cut), right_y, MM(2.7)),
      corner(right, Total(right_y, cut), MM(17)),
      corner(right, height, MM(2.8)),
      corner(tab, height),
      corner(tab, Total(left_y, tab), MM(7.5)),
      corner(ZERO, left_y, MM(9.4)),
      FolderSpine(Total(tab, width), ZERO, height, spine)
    )
  }),

  // A pocket off the back's outer edge, rounded, with two slits for a
  // business card; its glue tab under it. Round a spine the pocket is wider
  // by its gussets, the slits as far from its fold (Futura Bold, 5 mm: 180
  // mm).
  folder("futura_light", [MM(220), MM(305)], (width, height, spine) => {
    // Wider by its gussets, never past the back it folds onto
    const pocket_width = Min(Total(Scale(width, S.futura_light.pocket[0]), spine, spine), width)
    const pocket_height = Scale(height, S.futura_light.pocket[1])
    const x = Total(pocket_width, spine)
    const pocket = FolderFlapOff("left", pocket_width, {
      gusset: spine,
      round: [MM(65), ZERO],
      end: glue(MM(S.futura_light.glue), spine),
    })(at(x, height), at(x, Subtract(height, pocket_height)))
    return FolderDieOf(
      Total(x, width, width, spine),
      Total(height, spine, MM(S.futura_light.glue)),
      covers(x, ZERO, width, height, spine, { left: Constant(pocket) }),
      FolderCuts(
        ...FolderCardSlits(
          pocket.corners,
          // Where they are by the pocket's fold, past its gussets.
          at(Total(Scale(pocket_width, 0.5), spine), Total(Subtract(height, MM(56.5)), spine)),
          "tr_bl",
          50 as Degree,
          [MM(38.1), MM(22.8)]
        )
      )
    )
  }),

  // A pocket on a gusset, its glue tab under it; a pen holder cut across a
  // spine thick enough.
  folder("calibri", [MM(225), MM(310)], (width, height, spine) => {
    const [pocket_width, pocket_height] = [
      Scale(width, S.calibri.pocket[0]),
      Scale(height, S.calibri.pocket[1]),
    ]
    const x = Total(pocket_width, spine)
    // The glue tab drawn clockwise, its taper the other way round to the
    // grow's
    const [glue_start, glue_end] = S.calibri.glue_taper
    const pocket = FolderFlapOff("left", pocket_width, {
      gusset: spine,
      round: [MM(68), ZERO],
      end: glue(MM(S.calibri.glue), spine, [MM(glue_start), MM(glue_end)]),
    })(at(x, height), at(x, Subtract(height, pocket_height)))
    const [pen, loop] = [Total(x, width), [MM(60.1), MM(90.1)] as const]
    return FolderDieOf(
      Total(x, width, width, spine),
      Total(height, spine, MM(S.calibri.glue)),
      covers(x, ZERO, width, height, spine, { left: Constant(pocket) }),
      ...(spine >= MM(6)
        ? [
            FolderCuts(...loop.map(y => FolderLine(at(pen, y), at(Total(pen, spine), y)))),
            FolderSpine(Total(pen, Scale(spine, 0.5)), loop[0], loop[1], ZERO),
          ]
        : [])
    )
  }),

  // A quarter round pocket locked by a tab under the back into a slit.
  folder(
    "frutiger",
    [MM(S.frutiger.cover[0]), MM(S.frutiger.cover[1])],
    frutiger([MM(S.frutiger.cover[0]), MM(S.frutiger.cover[1])], {
      pocket: [MM(S.frutiger.pocket[0]), MM(S.frutiger.pocket[1]), MM(155)],
      flap: [MM(S.frutiger.flap[0]), MM(S.frutiger.flap[1]), MM(5)],
      tab: [MM(S.frutiger.tab[0]), MM(S.frutiger.tab[1]), MM(6)],
      slit: [MM(81.2), MM(11.6)],
    })
  ),

  // A quarter round pocket with a thumb cut, a half round flap with slits
  // for a business card, each on a gusset.
  folder("bookman", [MM(220), MM(311)], (width, height, spine) => {
    const [pocket_width, pocket_height] = [
      Scale(width, S.bookman.pocket[0]),
      Scale(height, S.bookman.pocket[1]),
    ]
    const x = Total(pocket_width, spine)
    const pocket = FolderFlapOff("left", pocket_width, { gusset: spine, round: [MM(205), ZERO] })(
      at(x, height),
      at(x, Subtract(height, pocket_height))
    )
    const inset = MM(S.bookman.flap_inset)
    const radius = Scale(Subtract(width, MM(2 * S.bookman.flap_inset)), 0.5)
    const flap = FolderFlapOff("bottom", radius, { gusset: spine, round: [ROUND, ROUND] })(
      at(Subtract(Total(x, width), inset), height),
      at(Total(x, inset), height)
    )
    // The thumb cut, an arc round its centre.
    const thumb = at(
      Scale(pocket_width, 83 / 209.8),
      Subtract(height, Scale(pocket_height, 554 / 1045))
    )
    const thumb_radius = MM(17.4)
    const inside = FolderInside(pocket.corners, MM(2))
    const fits =
      inside(at(Subtract(thumb.x, thumb_radius), Subtract(thumb.y, thumb_radius))) &&
      inside(at(Total(thumb.x, thumb_radius), Total(thumb.y, thumb_radius)))
    return FolderDieOf(
      Total(x, width, width, spine),
      Total(height, spine, radius),
      covers(x, ZERO, width, height, spine, { bottom: Constant(flap), left: Constant(pocket) }),
      FolderCuts(
        ...(fits
          ? [
              {
                points: ArcPoints(thumb, thumb_radius, 169.5 as Degree, -192.2 as Degree),
                closed: false,
              },
            ]
          : []),
        ...FolderCardSlits(
          flap.corners,
          at(Total(x, Scale(width, 0.5)), Total(height, spine, MM(45.4))),
          "tr_bl",
          45 as Degree,
          [MM(35), MM(19.9)]
        )
      )
    )
  }),

  // A side flap and a bottom flap on the back: a slit in the side one locks
  // the bottom one, slits in that hold a business card.
  folder("avant_garde", [MM(220), MM(310)], (width, height, spine) => {
    const spec = S.avant_garde
    const [side_depth, bottom_depth] = [Scale(width, spec.side), Scale(height, spec.bottom)]
    const x = Total(side_depth, spine)
    const base = Subtract(width, MM(spec.bottom_short))
    const side = FolderFlapOff("left", side_depth, {
      gusset: spine,
      taper: [Scale(side_depth, spec.side_taper[0]), Scale(side_depth, spec.side_taper[1])],
    })(at(x, height), at(x, ZERO))
    const bottom = FolderFlapOff("bottom", bottom_depth, {
      gusset: spine,
      taper: [Scale(bottom_depth, spec.bottom_taper[0]), Scale(bottom_depth, spec.bottom_taper[1])],
    })(at(Total(x, base), height), at(x, height))
    return FolderDieOf(
      Total(x, width, width, spine),
      Total(height, spine, bottom_depth),
      covers(x, ZERO, width, height, spine, { bottom: Constant(bottom), left: Constant(side) }),
      FolderCuts(
        ...FolderLines(side.corners, [
          at(Subtract(side_depth, MM(61.4)), Subtract(height, MM(78.4))),
          at(Subtract(side_depth, MM(2.8)), Subtract(height, MM(26.9))),
        ]),
        ...FolderCardSlits(
          bottom.corners,
          at(Total(x, Scale(base, 0.412)), Total(height, spine, MM(36.65))),
          "tl_br",
          45 as Degree,
          [MM(36.35), MM(22.3)]
        )
      )
    )
  }),

  folder("trinite", [MM(219.3), MM(300.1)], (width, height, spine) => {
    const spec = S.trinite
    const [side_depth, bottom_depth] = [Scale(width, spec.side), Scale(height, spec.bottom)]
    const x = Total(side_depth, spine)
    const base = Subtract(width, MM(spec.bottom_short))
    const side_taper = Scale(side_depth, spec.side_taper)
    const side = FolderFlapOff("left", side_depth, {
      gusset: spine,
      taper: [side_taper, side_taper],
    })(at(x, height), at(x, ZERO))
    const bottom_taper = Scale(bottom_depth, spec.bottom_taper)
    const bottom = FolderFlapOff("bottom", bottom_depth, {
      gusset: spine,
      taper: [bottom_taper, bottom_taper],
    })(at(Total(x, base), height), at(x, height))
    return FolderDieOf(
      Total(x, width, width, spine),
      Total(height, spine, bottom_depth),
      covers(x, ZERO, width, height, spine, { bottom: Constant(bottom), left: Constant(side) }),
      FolderCuts(
        ...FolderLines(side.corners, [
          at(Subtract(side_depth, MM(33.2)), Subtract(height, MM(36.4))),
          at(Subtract(side_depth, MM(7.9)), Subtract(height, MM(19.5))),
        ]),
        ...FolderCardSlits(
          bottom.corners,
          at(Total(x, Scale(base, 0.5)), Total(height, spine, MM(38.65))),
          "top"
        )
      )
    )
  }),

  // A side flap running out from the spine's top to the back's bottom, a
  // bottom flap hooked into slits in it.
  folder("hetfy", [MM(225), MM(314.8)], (width, height, spine) => {
    const spec = S.hetfy
    const [side_depth, bottom_depth] = [Scale(width, spec.side), Scale(height, spec.bottom)]
    const x = Total(side_depth, spine)
    const side_top = Scale(height, spec.side_top)
    const side = FolderFlapOff("left", side_depth, {
      gusset: spine,
      taper: [
        Min(Scale(height, spec.side_cut), Subtract(height, Total(side_top, MM(spec.side_least)))),
        ZERO,
      ],
    })(at(x, height), at(x, side_top))
    // The hook, along the bottom flap's 45° side from its tip to its base, from
    // the base.
    const base = at(x, Total(height, spine))
    const HOOK = [
      [MM(52.5), MM(51)],
      [MM(52.5), MM(55)],
      [MM(32.3), MM(65.3)],
      [MM(18.6), MM(53)],
      [MM(28.5), MM(32.5)],
      [MM(33.7), MM(32.5)],
    ] as const
    const hook: FolderSide = (a, b) =>
      FolderChain(
        corner(a.x, a.y),
        ...(bottom_depth >= MM(75)
          ? HOOK.map(([dx, dy]) => corner(Total(base.x, dx), Total(base.y, dy)))
          : []),
        corner(b.x, b.y)
      )
    const bottom = FolderFlapOff("bottom", bottom_depth, {
      gusset: spine,
      taper: [bottom_depth, Scale(bottom_depth, spec.bottom_taper)],
      start: hook,
    })(at(Subtract(Total(x, width), MM(spec.bottom_short)), height), at(x, height))
    return FolderDieOf(
      Total(x, width, width, spine),
      Total(height, spine, bottom_depth),
      covers(x, ZERO, width, height, spine, {
        bottom: Constant(bottom),
        left: (_, b) => FolderChain(side, corner(b.x, b.y)),
      }),
      FolderCuts(
        ...FolderLines(
          side.corners,
          [
            at(Subtract(side_depth, MM(53.7)), Subtract(height, MM(52.6))),
            at(Subtract(side_depth, MM(29.4)), Subtract(height, MM(28.5))),
          ],
          [
            at(Subtract(side_depth, MM(31.2)), Subtract(height, MM(66.3))),
            at(Subtract(side_depth, MM(15.3)), Subtract(height, MM(50.4))),
          ]
        ),
        ...FolderLines(bottom.corners, [
          at(Total(base.x, MM(86.3)), Total(base.y, MM(9.5))),
          at(Total(base.x, MM(102.2)), Total(base.y, MM(25.4))),
        ])
      )
    )
  }),

  // Three flaps round the back.
  folder(
    "century_gothic",
    [MM(220), MM(310.8)],
    three_flaps(
      {
        depth: S.century_gothic.side,
        taper: S.century_gothic.side_taper,
        round: [ZERO, ZERO],
        inset: [ZERO, ZERO],
        bevel: [false, false],
      },
      {
        depth: S.century_gothic.ends,
        taper: S.century_gothic.ends_taper,
        round: [ZERO, ZERO],
        inset: [ZERO, ZERO],
        bevel: [false, false],
      }
    )
  ),

  folder(
    "brandon",
    [MM(231), MM(321)],
    three_flaps(
      {
        depth: S.brandon.side,
        taper: S.brandon.side_taper,
        round: [ZERO, ZERO],
        inset: [ZERO, ZERO],
        bevel: [false, false],
      },
      {
        depth: S.brandon.ends,
        taper: S.brandon.ends_taper,
        round: [ZERO, ZERO],
        inset: [MM(S.brandon.inset[0]), MM(S.brandon.inset[1])],
        bevel: [false, false],
      }
    )
  ),

  // Rounded flaps on creased gussets, the top and bottom ones cut at 45° by
  // the spine; the back is wider than the front.
  folder(
    "impact",
    [MM(235.1), MM(332.7)],
    three_flaps(
      {
        depth: S.impact.side,
        taper: [0, 0],
        round: [MM(33), MM(33)],
        inset: [ZERO, ZERO],
        bevel: [false, false],
      },
      {
        depth: S.impact.ends,
        taper: S.impact.ends_taper,
        round: [MM(40), ZERO],
        inset: [MM(S.impact.inset[0]), MM(S.impact.inset[1])],
        bevel: [false, true],
      },
      true,
      MM(S.impact.extra)
    )
  ),

  // A panel folded over the back, a flap under it.
  folder("alieron", [MM(220.4), MM(310)], (width, height, spine, sheet) => {
    const spec = S.alieron
    const [panel, bottom_depth] = [Scale(width, spec.panel), Scale(height, spec.bottom)]
    // The flap's gusset short of the panel's, under it
    const under = Max(ZERO, Subtract(spine, Scale(sheet, S.under_panel)))
    const x = Total(panel, spine)
    return FolderDieOf(
      Total(x, width, width, spine),
      Total(height, under, bottom_depth),
      covers(x, ZERO, width, height, spine, {
        bottom: FolderFlapOff("bottom", bottom_depth, {
          gusset: under,
          taper: [
            Scale(bottom_depth, spec.bottom_taper[0]),
            Scale(bottom_depth, spec.bottom_taper[1]),
          ],
        }),
        left: FolderFlapOff("left", panel, { gusset: spine }),
      })
    )
  }),

  // Three panels, rounded pockets glued under the back or both.
  folder(
    "castellar",
    [MM(220), MM(305.2)],
    pockets(S.castellar.panel, { width: S.castellar.pocket, glue: MM(S.castellar.glue) }, undefined)
  ),

  // Castellar's, a pocket under the front too; its .eps is drawn the other
  // way round to its preview: the front left, the back and its panel right.
  folder("rotis", [MM(220), MM(305.2)], (width, height, spine, sheet) =>
    FolderMirror(
      pockets(
        S.rotis.panel,
        { width: S.rotis.back.pocket, glue: MM(S.rotis.back.glue) },
        { width: S.rotis.front.pocket, glue: MM(S.rotis.front.glue) }
      )(width, height, spine, sheet)
    )
  ),

  // Two panels, a pocket under each, glue tabs out of the sides.
  folder(
    "univers",
    [MM(220), MM(305.2)],
    pockets(
      0,
      { width: S.univers.back.pocket, glue: MM(S.univers.back.glue) },
      { width: S.univers.front.pocket, glue: MM(S.univers.front.glue) }
    )
  ),
] as const)
